import { Fragment, useMemo } from 'react';
import StyledButton from './StyledButton';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from '@/i18n';

type Props = {
  page: number;
  setPage: (value: React.SetStateAction<number>) => void;
  pageSize: number;
  dataLength: number;
  label: string;
  pluralLabel?: string;
  minimized?: boolean;
};

const StyledNextPreviousButtons = ({
  page,
  setPage,
  pageSize,
  dataLength,
  label,
  minimized = false,
  pluralLabel,
}: Props) => {
  const { t } = useTranslation();

  const hasMore = useMemo(
    () => (dataLength ? dataLength > page * pageSize : false),
    [dataLength, page, pageSize],
  );

  const nbPages = useMemo(
    () => Math.ceil(dataLength / pageSize) || 1,
    [dataLength, pageSize],
  );

  const pages = useMemo(() => {
    if (nbPages <= 5) return Array.from({ length: nbPages }, (_, i) => i + 1);
    const displayedPages = Array.from({ length: 3 }, (_, i) => {
      if (page === 1) return page + i;
      if (page === nbPages) return page - 2 + i;
      else return page - 1 + i;
    });
    if (page >= nbPages - 1) displayedPages.unshift(2);
    if (page > 2) displayedPages.unshift(1);
    if (page <= 2) displayedPages.push(nbPages - 1);
    if (page < nbPages - 1) displayedPages.push(nbPages);
    return displayedPages;
  }, [nbPages, page]);

  const plural = useMemo(
    () => pluralLabel || label + 's',
    [label, pluralLabel],
  );

  return (
    <div
      className={clsx('flex items-top w-full gap-2', {
        'justify-between': nbPages > 1,
        'justify-center': nbPages === 1,
      })}
    >
      <StyledButton
        title={!minimized ? t('common.previous') : ''}
        variant="link"
        className={clsx(
          'px-3 text-gray-800 border-gray-300 disabled:text-gray-500',
          {
            'bg-white border rounded-lg': minimized && page > 1,
            hidden: nbPages === 1,
          },
        )}
        onClick={() => (page > 1 ? setPage(page - 1) : null)}
        disabled={page === 1}
        icon={<ArrowLeftIcon className="size-5 stroke-gray-700" />}
      />
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center justify-center gap-2">
          {!minimized ? (
            pages.map((pageNumber, index) => (
              <Fragment key={`pageNumber-${index}`}>
                {pageNumber !== 1 && !pages.includes(pageNumber - 1) && (
                  <span>...</span>
                )}
                <StyledButton
                  variant="link"
                  className={clsx('px-3.5 py-2', {
                    'rounded-lg border border-gray-200': pageNumber === page,
                  })}
                  title={pageNumber.toString()}
                  onClick={() => setPage(pageNumber)}
                  disabled={pageNumber === page}
                />
              </Fragment>
            ))
          ) : (
            <span className="text-sm font-medium text-gray-700">
              {t('common.page')} {page} {t('common.of')} {nbPages}
            </span>
          )}
        </div>
        <span
          className={clsx('text-gray-700', {
            'text-sm font-medium': !minimized,
            'text-xs font-normal': minimized,
          })}
        >
          {dataLength
            ? dataLength === 1
              ? `${t('common.one')} ${label}`
              : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, dataLength)} ${t('common.of')} ${dataLength} ${plural}`
            : t('common.noDataFound')}
        </span>
      </div>
      <StyledButton
        title={!minimized ? t('common.next') : ''}
        variant="link"
        className={clsx(
          'px-3 text-gray-800 border-gray-300 disabled:text-gray-500',
          {
            'bg-white border rounded-lg': minimized && hasMore,
            hidden: nbPages === 1,
          },
        )}
        onClick={() => (hasMore ? setPage(page + 1) : null)}
        icon={<ArrowRightIcon className="size-5 stroke-gray-700" />}
        iconPosition="right"
        disabled={!hasMore}
      />
    </div>
  );
};

export default StyledNextPreviousButtons;
